// src/components/Homescreen3DPub.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Box, 
    CircularProgress, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle, 
    IconButton, 
    Link, 
    Typography,
    Button
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as THREE from 'three';
import { getPubRaycasting, clearTilesBySession } from '../API';
import MultiMeshViewer from './MultiMeshViewer';
import { convertFromExternalCoords } from '../utils/threeUtils';

interface Homescreen3DPubProps {
  googlePlaceId: string;
  onError?: () => void;
  onClick?: () => void;
  onSessionReady?: (cleanupFunction: () => void) => void;
}

interface SceneData {
  meshUris: string[];
  gardenPolygons: { [key: string]: { polygon: [number, number][], elevation: number | null } };
  sunVectors: { [timestamp:string]: [number, number, number] };
  focalPoint: [number, number, number];
}

const Homescreen3DPub: React.FC<Homescreen3DPubProps> = ({ googlePlaceId, onError, onClick, onSessionReady }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sceneData, setSceneData] = useState<SceneData | null>(null);
  const [animatedSunVector, setAnimatedSunVector] = useState<[number, number, number] | undefined>();
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [copyrights, setCopyrights] = useState<string[]>([]);
  const sessionIdRef = useRef<string | null>(null);

  const handleCopyrightsLoaded = useCallback((loadedCopyrights: string[]) => {
    setCopyrights(loadedCopyrights);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const raycastResponse = await getPubRaycasting(googlePlaceId, false);
        const { data } = raycastResponse;

        if (!data.mesh_uris || data.mesh_uris.length === 0 || !data.session_id || !data.sun_vector || !data.garden_polygons) {
          throw new Error("API response is missing required data for 3D preview.");
        }
        
        const allPoints: [number, number][] = [];
        let totalElevation = 0;
        let elevationCount = 0;

        Object.values(data.garden_polygons as SceneData['gardenPolygons']).forEach(garden => {
            if (garden.polygon) allPoints.push(...garden.polygon);
            if (garden.elevation !== null) {
                totalElevation += garden.elevation;
                elevationCount++;
            }
        });

        if (allPoints.length === 0) {
            throw new Error("Cannot determine focal point: No garden points available.");
        }

        const avgLat = allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length;
        const avgLon = allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length;
        const avgElevation = elevationCount > 0 ? totalElevation / elevationCount : 0;
        
        const focalPointEcef = convertFromExternalCoords([avgLat, avgLon, avgElevation]);
        const focalPoint: [number, number, number] = [focalPointEcef.x, focalPointEcef.y, focalPointEcef.z];
        
        const googleTilesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!googleTilesApiKey) {
            throw new Error("Google Maps API key is not configured.");
        }

        const cleanSessionId = data.session_id.startsWith('session=') ? data.session_id.substring(8) : data.session_id;
        sessionIdRef.current = cleanSessionId;
        
        const completeMeshUris = data.mesh_uris.map((uri: string) => {
            const uriPath = uri.startsWith('/') ? uri : `/${uri}`;
            return `https://tile.googleapis.com${uriPath}?session=${cleanSessionId}&key=${googleTilesApiKey}`;
        });
        
        const newSceneData: SceneData = {
          meshUris: completeMeshUris,
          gardenPolygons: data.garden_polygons,
          sunVectors: data.sun_vector,
          focalPoint: focalPoint,
        };
        setSceneData(newSceneData);
        
        // Provide cleanup function to parent
        if (onSessionReady) {
          const cleanup = () => {
            if (sessionIdRef.current) {
              clearTilesBySession(sessionIdRef.current);
              sessionIdRef.current = null;
            }
          };
          onSessionReady(cleanup);
        }

      } catch (err: any) {
        setError(err.message || "Could not load 3D preview.");
        if (onError) onError();
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    
    // Cleanup: Only clear tiles when googlePlaceId changes, not on every unmount
    // This allows the component to persist its 3D state when navigating back to home
    return () => {
      // Only clear session tiles if we're switching to a different pub
      // For the home screen, we want to keep the tiles cached in memory
    };
  }, [googlePlaceId, onError]);

  useEffect(() => {
    if (!sceneData?.sunVectors) return;

    const timeKeys = Object.keys(sceneData.sunVectors).sort();
    if (timeKeys.length === 0) return;

    let currentIndex = 0;
    let direction = 1;
    let progress = 0;
    let animationId: number;
    const animationSpeed = 0.03;
    
    const animate = () => {
      const currentTime = timeKeys[currentIndex];
      const nextIndex = Math.min(currentIndex + 1, timeKeys.length - 1);
      const nextTime = timeKeys[nextIndex];
      
      const currentSunVector = new THREE.Vector3(...sceneData.sunVectors[currentTime]);
      const nextSunVector = new THREE.Vector3(...sceneData.sunVectors[nextTime]);
      const interpolatedVector = new THREE.Vector3().lerpVectors(currentSunVector, nextSunVector, progress);
      
      setAnimatedSunVector(interpolatedVector.toArray() as [number, number, number]);
      
      progress += animationSpeed * direction;
      if (progress >= 1) {
        progress = 0;
        currentIndex += 1;
        if (currentIndex >= timeKeys.length - 1) {
          direction = -1;
          currentIndex = timeKeys.length - 1;
        }
      } else if (progress <= 0 && direction === -1) {
        progress = 1;
        currentIndex -= 1;
        if (currentIndex <= 0) {
          direction = 1;
          currentIndex = 0;
        }
      }
      
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => { cancelAnimationFrame(animationId); };
  }, [sceneData]);


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white' }}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error || !sceneData) {
    console.error("Error loading 3D pub:", error);
    return null;
  }

  return (
    <>
      <Box 
        sx={{ 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          cursor: 'pointer'
        }}
        onClick={onClick}
      >
        <Box sx={{ pointerEvents: 'none', width: '100%', height: '100%' }}>
          <MultiMeshViewer
            meshUris={sceneData.meshUris}
            focalPoint={sceneData.focalPoint}
            gardenPolygons={sceneData.gardenPolygons}
            sunEcefVector={animatedSunVector}
            height="100%"
            initialZoom={50}
            onCopyrightsLoaded={handleCopyrightsLoaded}
          />
        </Box>
         <Box sx={{
          position: 'absolute',
          bottom: 1,
          left: 30,
          right: 30,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pointerEvents: 'none' // Let clicks pass through the container, except for the button
        }}>
          <Typography sx={{
            color: 'white',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            pl: 1
          }}>
            Google
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation(); // Prevents the parent 'find pubs near me' from triggering
              e.preventDefault(); // Prevents any default behavior
              setInfoModalOpen(true);
            }}
            sx={{
              color: 'white',
              pointerEvents: 'auto', // Make this button clickable
              padding: '4px', // Smaller padding to make the button smaller
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              }
            }}
          >
            <InfoOutlinedIcon fontSize="inherit" style={{ fontSize: '16px' }} />
          </IconButton>
        </Box>
      </Box>

      <Dialog open={infoModalOpen} onClose={() => setInfoModalOpen(false)} PaperProps={{ sx: { borderRadius: 3, m: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>About This View</DialogTitle>
        <DialogContent>
            <DialogContentText component="div" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>The Wickham Arms</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    We're very happy to showcase the <a href="https://www.instagram.com/thewickhamarmspub/?hl=en" target="_blank" rel="noopener noreferrer">Wickham Arms</a> (69 Upper Brockley Rd, London SE4 1TF) as our first featured pub. 
                    <br />
                    <br />
                    If you're interested in being featured on our homepage, please get in touch at <a href="mailto:hello@sun-tap.com">hello@sun-tap.com</a>
                </Typography>

            </DialogContentText>
            <DialogContentText component="div">
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>Data Sources</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    This 3D view is powered by Google services. All map data is subject to Google's terms and conditions.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Homescreen 3D Tiles copyright (sorted in order of appearance): 
                </Typography>
                {copyrights.map((line, index) => (
                    <Typography key={index} variant="caption" display="block" color="text.secondary">
                        {line}
                    </Typography>
                ))}
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setInfoModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Homescreen3DPub;